import type { ActivityAction, ActivityLogEntry } from "@/types";

/** Métadonnées d'affichage d'une nature d'action. */
export interface ActionMeta {
  label: string;
  icon: string;
  tone: "primary" | "accent" | "secondary" | "neutral" | "success" | "danger";
}

/** Libellé, icône et teinte associés à chaque nature d'action. */
export const actionMeta: Record<ActivityAction, ActionMeta> = {
  CREATE: { label: "Création", icon: "ri-add-circle-line", tone: "success" },
  UPDATE: { label: "Modification", icon: "ri-edit-line", tone: "primary" },
  DELETE: { label: "Suppression", icon: "ri-delete-bin-line", tone: "danger" },
  LOGIN: { label: "Connexion", icon: "ri-login-box-line", tone: "secondary" },
};

/** Options pour un sélecteur de nature d'action. */
export const actionOptions: { value: ActivityAction; label: string }[] = (
  Object.keys(actionMeta) as ActivityAction[]
).map((key) => ({ value: key, label: actionMeta[key].label }));

/** Icône associée à chaque module du journal. */
export const activityModuleIcons: Record<string, string> = {
  Connexion: "ri-login-box-line",
  Élèves: "ri-user-3-line",
  Inscriptions: "ri-file-add-line",
  Paiements: "ri-hand-coin-line",
  Finances: "ri-wallet-3-line",
  Dépenses: "ri-arrow-down-circle-line",
  Classes: "ri-building-4-line",
  Matières: "ri-book-2-line",
  Enseignants: "ri-user-star-line",
  Personnel: "ri-team-line",
  Utilisateurs: "ri-group-line",
  Rôles: "ri-shield-keyhole-line",
  Paramètres: "ri-settings-3-line",
  Rapports: "ri-line-chart-line",
};

/** Retourne l'icône d'un module (icône par défaut si inconnu). */
export function moduleIcon(module: string): string {
  return activityModuleIcons[module] ?? "ri-record-circle-line";
}

/** Clé de journée locale (AAAA-MM-JJ) d'un horodatage ISO. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Libellé lisible d'une journée : « Aujourd'hui », « Hier » ou date complète. */
export function dayLabel(key: string): string {
  const d = new Date(`${key}T00:00:00`);
  if (Number.isNaN(d.getTime())) return key;
  const today = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(today) - startOf(d)) / 86400000);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return "Hier";
  return d.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/** Icône de l'appareil : téléphone, tablette ou ordinateur. */
export function deviceIcon(device: string): string {
  if (/téléphone|iPhone/.test(device)) return "ri-smartphone-line";
  if (/tablette|iPad/.test(device)) return "ri-tablet-line";
  return device ? "ri-computer-line" : "ri-router-line";
}

/** Heure au format HH:MM à partir d'un horodatage ISO. */
export function timeLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}
/** Télécharge le journal affiché au format CSV (séparateur « ; », lisible par Excel). */
export function downloadActivityCsv(entries: ActivityLogEntry[], filename: string, schoolNames: Record<string, string> = {}): void {
  const cell = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const header = ["Date", "Heure", "École", "Auteur", "Rôle", "Module", "Action", "Description", "Cible", "Appareil", "IP"];
  const rows = entries.map((e) =>
    [
      dayKey(e.createdAt),
      timeLabel(e.createdAt),
      e.schoolId ? schoolNames[e.schoolId] ?? e.schoolId : "Plateforme",
      e.actorName,
      e.actorRole,
      e.module,
      actionMeta[e.action].label,
      e.description,
      e.target ?? "",
      e.device,
      e.ip,
    ].map(cell).join(";"),
  );
  const blob = new Blob(["﻿" + [header.map(cell).join(";"), ...rows].join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
